import { useState } from 'react';

import { ContentCopy as CopyIcon, Print as PrintIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { IssuedCredentials, ResidentCredentials } from '@/lib/api/credentials';
import { PROGRAM_LABEL } from '@/lib/program-labels';

/** 비밀번호를 잃어버렸을 때 할 수 있는 일은 재발급뿐이라는 안내 */
export const LOST_PASSWORD_HINT =
  '비밀번호는 저장되지 않아 다시 볼 수 없습니다. 잃어버렸다면 재발급하셔야 합니다.';

interface Props {
  open: boolean;
  title: string;
  credentials: ResidentCredentials | IssuedCredentials | null;
  /** 재발급 버튼 — 넘기지 않으면 버튼을 그리지 않는다 */
  onResetPassword?: () => void;
  resetting?: boolean;
  onClose: () => void;
}

function hasTemporaryPassword(c: ResidentCredentials | IssuedCredentials): c is IssuedCredentials {
  return 'voiceTemporaryPassword' in c && typeof c.voiceTemporaryPassword === 'string';
}

function Field({ label, value }: { label: string; value: string | null }) {
  const [copied, setCopied] = useState(false);
  const text = value ?? '—';

  const copy = () => {
    if (!value) return;
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 84, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', flex: 1 }}>
        {text}
      </Typography>
      {value && (
        <Tooltip title={copied ? '복사됨' : '복사'}>
          <IconButton size="small" onClick={copy} aria-label={`${label} 복사`}>
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * 비밀번호 자리 — 발급 직후에만 실제 값, 그 뒤로는 블러 처리한 가짜 값.
 *
 * 빈칸이나 '—'로 두면 "비밀번호가 없는 계정"으로 읽힌다. 가려진 채로 자리를 지키고
 * 있어야 '있긴 한데 볼 수 없다'가 전달된다. 마우스를 올리면 왜 못 보는지와
 * 무엇을 해야 하는지를 알려 준다.
 */
function PasswordField({ revealed }: { revealed: string | null }) {
  if (revealed) {
    return <Field label="비밀번호" value={revealed} />;
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 84, flexShrink: 0 }}>
        비밀번호
      </Typography>
      <Tooltip title={LOST_PASSWORD_HINT} placement="top">
        <Box
          aria-label="비밀번호 숨김"
          sx={{
            flex: 1,
            cursor: 'help',
            userSelect: 'none',
            filter: 'blur(4px)',
            opacity: 0.55,
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: 1,
          }}
        >
          ••••••••••
        </Box>
      </Tooltip>
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * 입소자 로그인 정보 표시.
 *
 * 계정 발급 직후 1회와, 이후 상세 패널의 '자격증명 보기' 양쪽에서 같은 화면을 쓴다.
 * 다른 점은 보이스웰니스 비밀번호 한 줄뿐이다 — 발급 직후에만 보이고 그 뒤로는
 * 블러 처리된다.
 */
export function CredentialsDialog({
  open,
  title,
  credentials,
  onResetPassword,
  resetting,
  onClose,
}: Props) {
  const temporaryPassword =
    credentials && hasTemporaryPassword(credentials) ? credentials.voiceTemporaryPassword : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {credentials && (
          <Stack spacing={2} divider={<Divider flexItem />} sx={{ pt: 0.5 }}>
            <Section title={PROGRAM_LABEL.bgt.full}>
              <Field label="환자번호" value={credentials.bgtPatientCode} />
              <Field label="생년월일" value={credentials.bgtBirthDate} />
            </Section>

            <Section title={PROGRAM_LABEL.voice.full}>
              {credentials.voiceLoginId ? (
                <>
                  <Field label="아이디" value={credentials.voiceLoginId} />
                  <PasswordField revealed={temporaryPassword} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  연결된 계정이 없습니다.
                </Typography>
              )}
            </Section>

            {temporaryPassword && (
              <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
                <strong>비밀번호는 이 창에서만 볼 수 있습니다.</strong> 닫은 뒤에는 다시 꺼낼 수
                없고 재발급만 가능합니다. 환자번호·생년월일·아이디는 언제든 다시 볼 수 있습니다.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          size="small"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ mr: 'auto' }}
        >
          인쇄
        </Button>
        {onResetPassword && credentials?.voiceLoginId && (
          <Button size="small" color="warning" onClick={onResetPassword} disabled={resetting}>
            {resetting ? '재발급 중…' : '비밀번호 재발급'}
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
}
